import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Spinner,
  Form,
  Modal,
  Tabs,
  Tab,
  Row,
  Col,
} from '@/components/ui';
import { Link } from 'react-router-dom';
import {
  workIdService,
  type WorkIdDetail,
  type WorkIdTaskSummary,
  type WorkIdFilterOptions,
} from '../../../services/workIdService';
import {
  type PcrSubmission,
  patchPcrSubmission,
  formatPcrStatusLabel,
  normalizePcrReportTypeBucket,
} from '../../../services/pcrService';
import { assetService } from '../../../services/assetService';
import { routineTaskService } from '../../../services/routineTaskService';
import { taskService as quantifiedTaskService } from '../../../services/quantifiedTaskService';
import ResponsiveModal from '../../../components/ResponsiveModal';
import GoogleGPSMap from '../../../components/GoogleGPSMap';
import WorkIdStatusProgress from './WorkIdStatusProgress';
import WorkIdImageAttachments from './WorkIdImageAttachments';
import WorkIdHistoryModal from './WorkIdHistoryModal';
import WorkIdApproveModal from './WorkIdApproveModal';
import WorkIdCancelModal from './WorkIdCancelModal';
import WorkIdReopenModal from './WorkIdReopenModal';
import WorkIdUnmergeModal from './WorkIdUnmergeModal';
import PcrSubmissionDetailModal from '../../pcr-submissions/components/PcrSubmissionDetailModal';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { WorkIdStatus } from '../utils/workIdStatusConstants';
import { taskPageUrl } from '../utils/taskPageUrl';
import type { Asset } from '../../../types/Asset';
import { getPriorityVariant, formatPriority } from '../utils/priorityDisplay';
import { ASSET_TYPES, ASSET_TYPE_LABELS } from '../../../constants/assetTypes';
import { formatDate, formatDate as formatPacificDate, formatDateTime } from '@/utils/displayDateTime';

/** Row count for read-only comments textarea; min 1 avoids extra blank space for short text. */
function readOnlyCommentsTextareaRows(text: string | null | undefined): number {
  if (text == null || text === '') return 1;
  const explicitLines = text.split('\n').length;
  const wrappedApprox = Math.ceil(text.length / 100);
  return Math.min(18, Math.max(1, Math.max(explicitLines, wrappedApprox)));
}

/**
 * UTC calendar YYYY-MM-DD for `<input type="date" />` from an API ISO string.
 * Matches server storage (`Date.UTC` for YYYY-MM-DD) so the picked day does not shift by timezone.
 */
function reportedOnToDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Human-readable reported date using the UTC calendar day (same semantics as the date picker). */
function formatReportedOnDisplay(iso: string | null | undefined): string {
  return formatPacificDate(iso) || '–';
}

type TaskWithType = WorkIdTaskSummary;

/** Elapsed time from Work ID row creation in WRAP (updates every minute; not contractual response time). */
function WorkIdResponseTimer({ createdAtIso }: { createdAtIso: string | undefined | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  if (!createdAtIso) return null;
  const start = new Date(createdAtIso).getTime();
  if (Number.isNaN(start)) return null;
  const ms = Math.max(0, now - start);
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours || days) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return (
    <Form.Group className="tw-mb-0">
      <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Elapsed since Work ID created</Form.Label>
      <div className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2 tw-text-sm">
        <span className="tw-font-semibold tw-text-content">{parts.join(' ')}</span>
        <span className="tw-text-content-muted"> elapsed</span>
        <div className="tw-text-content-muted tw-mt-1" style={{ fontSize: '0.85rem' }}>
          Work ID record created{' '}
          {formatDateTime(createdAtIso)}
        </div>
        <div className="tw-text-content-muted tw-mt-1" style={{ fontSize: '0.8rem' }}>
          WRAP-only clock from save time – not a contractual service-area response-time specification.
        </div>
      </div>
    </Form.Group>
  );
}

const PRIORITY_OPTIONS = [
  { value: '', label: 'Select priority' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

interface WorkIdDetailModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
  /**
   * When true, the modal will open directly in edit mode
   * instead of view-only. Intended for the action-bar "Edit" flow.
   */
  startInEditMode?: boolean;
}

const WorkIdDetailModal: React.FC<WorkIdDetailModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
  startInEditMode = false,
}) => {
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  const [detail, setDetail] = useState<WorkIdDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskActionId, setTaskActionId] = useState<number | null>(null);
  const [taskUnassignId, setTaskUnassignId] = useState<number | null>(null);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [approveVisible, setApproveVisible] = useState(false);
  const [cancelVisible, setCancelVisible] = useState(false);
  const [reopenVisible, setReopenVisible] = useState(false);
  const [unmergeVisible, setUnmergeVisible] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [linkedTaskHover, setLinkedTaskHover] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'attachments' | 'pcrs'>('details');
  const [linkedPcrs, setLinkedPcrs] = useState<PcrSubmission[]>([]);
  const [linkedPcrsLoading, setLinkedPcrsLoading] = useState(false);
  const [selectedPcr, setSelectedPcr] = useState<PcrSubmission | null>(null);
  const [reasonModal, setReasonModal] = useState<{
    show: boolean;
    action: 'close' | 'reopen';
    task: TaskWithType | null;
    reason: string;
    error: string | null;
  }>({ show: false, action: 'close', task: null, reason: '', error: null });

  const [internalReviewModal, setInternalReviewModal] = useState<{
    show: boolean;
    taskId: number | null;
    comments: string;
    loading: boolean;
  }>({ show: false, taskId: null, comments: '', loading: false });

  const [editMode, setEditMode] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    workIdentificationId: '',
    comments: '',
    priority: '',
    foremanAreaId: '' as number | '',
    assetType: ASSET_TYPES.ROAD as string,
    assetId: '' as number | '',
    reportedOn: '',
  });
  const [filterOptions, setFilterOptions] = useState<WorkIdFilterOptions>({ serviceAreas: [], foremanAreas: [] });
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);

  const canUnmerge = hasPermission('workId:merge');
  const canAssign = hasPermission('workId:assign');
  const canEdit = hasPermission('workId:approve') || hasPermission('workId:create');
  const isAdmin = currentUser?.role?.name?.toLowerCase() === 'admin';
  const statusUpper = detail?.status?.toUpperCase() ?? '';
  const isEditableStatus = statusUpper === 'NEW' || statusUpper === 'APPROVED';
  const showEditButton = canEdit && isEditableStatus && !!detail;
  const isMerged = detail?.status?.toUpperCase() === 'MERGED';

  const loadDetail = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await workIdService.getById(id);
      setDetail(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load work ID');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visible && workId?.id) {
      setDetail(workId);
      setError(null);
      setShowMap(false);
      setActiveTab('details');
      setLinkedPcrs([]);
      setLinkedPcrsLoading(false);
      setSelectedPcr(null);
      setEditMode(startInEditMode);

      // When opened from the action menu in edit mode, prepopulate the edit form
      if (startInEditMode) {
        const asset = workId.asset as { assetType?: string } | null | undefined;
        setEditForm({
          workIdentificationId: workId.workIdentificationId ?? '',
          comments: workId.comments ?? '',
          priority: workId.priority ?? '',
          foremanAreaId: workId.foremanAreaId ?? '',
          assetType: asset?.assetType?.toLowerCase() ?? ASSET_TYPES.ROAD,
          assetId: workId.asset?.id ?? '',
          reportedOn: reportedOnToDateInputValue(workId.reportedOn),
        });
      }
    }
  }, [visible, workId, startInEditMode]);

  const taskStatusVariant = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'CLOSED':
        return 'secondary';
      case 'ACTIVE':
        return 'success';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'warning';
    }
  };

  const openReasonModal = (task: TaskWithType, action: 'close' | 'reopen') => {
    setReasonModal({ show: true, action, task, reason: '', error: null });
  };

  const closeReasonModal = () => {
    setReasonModal({ show: false, action: 'close', task: null, reason: '', error: null });
  };

  const handleCloseTask = async (task: TaskWithType, reason: string) => {
    if (!detail) return;
    setTaskActionId(task.id);
    setError(null);
    try {
      if (task.type === 'Routine') {
        await routineTaskService.updateTaskStatus(task.id, 'CLOSED', reason);
      } else if (task.type === 'Quantified') {
        await quantifiedTaskService.closeTask(task.id, {
          taskId: task.id,
          comments: reason,
        });
      }
      closeReasonModal();
      await loadDetail(detail.id);
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to close task');
    } finally {
      setTaskActionId(null);
    }
  };

  const handleReopenTask = async (task: TaskWithType, reason: string) => {
    if (!detail) return;
    setTaskActionId(task.id);
    setError(null);
    try {
      if (task.type === 'Routine') {
        await routineTaskService.updateTaskStatus(task.id, 'ACTIVE', reason);
      } else if (task.type === 'Quantified') {
        await quantifiedTaskService.reopenTask(task.id, {
          taskId: task.id,
          comments: reason,
        });
      }
      closeReasonModal();
      await loadDetail(detail.id);
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reopen task');
    } finally {
      setTaskActionId(null);
    }
  };

  const submitReasonModal = () => {
    const trimmed = reasonModal.reason.trim();
    if (!trimmed) {
      setReasonModal((prev) => ({ ...prev, error: 'Reason is required.' }));
      return;
    }
    if (!reasonModal.task) return;
    if (reasonModal.action === 'close') {
      handleCloseTask(reasonModal.task, trimmed);
    } else {
      handleReopenTask(reasonModal.task, trimmed);
    }
  };

  const handleSetToInternalReview = async () => {
    if (internalReviewModal.taskId == null || !detail) return;
    const comments = internalReviewModal.comments.trim();
    if (!comments) return;
    setInternalReviewModal((prev) => ({ ...prev, loading: true }));
    setError(null);
    try {
      await quantifiedTaskService.setToInternalReview(internalReviewModal.taskId, {
        comments,
        userId: currentUser?.id,
      });
      setInternalReviewModal({ show: false, taskId: null, comments: '', loading: false });
      await loadDetail(detail.id);
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to set task to internal review');
    } finally {
      setInternalReviewModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleRemoveFromTask = async (taskId: number) => {
    if (!detail) return;
    setTaskUnassignId(taskId);
    setError(null);
    try {
      await workIdService.unassign(detail.id, taskId);
      await loadDetail(detail.id);
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove Work ID from task');
    } finally {
      setTaskUnassignId(null);
    }
  };

  const handleActionSuccess = useCallback(() => {
    if (detail) loadDetail(detail.id);
    onSuccess();
  }, [detail, loadDetail, onSuccess]);

  const startEdit = useCallback(() => {
    if (!detail) return;
    const asset = detail.asset as { assetType?: string } | null | undefined;
    setEditForm({
      workIdentificationId: detail.workIdentificationId ?? '',
      comments: detail.comments ?? '',
      priority: detail.priority ?? '',
      foremanAreaId: detail.foremanAreaId ?? '',
      assetType: asset?.assetType?.toLowerCase() ?? ASSET_TYPES.ROAD,
      assetId: detail.asset?.id ?? '',
      reportedOn: reportedOnToDateInputValue(detail.reportedOn),
    });
    setEditMode(true);
    setError(null);
  }, [detail]);

  const cancelEdit = useCallback(() => {
    setEditMode(false);
  }, []);

  useEffect(() => {
    if (!editMode || !visible) return;
    const load = async () => {
      try {
        const options = await workIdService.getFilterOptions();
        setFilterOptions(options);
      } catch (e) {
        console.error('Error loading filter options:', e);
      }
    };
    load();
  }, [editMode, visible]);

  const editForemanArea = filterOptions.foremanAreas.find(
    (fa) => String(fa.id) === String(editForm.foremanAreaId)
  );
  const editServiceAreaId = editForemanArea?.serviceAreaId ?? detail?.foremanArea?.serviceArea?.id;

  useEffect(() => {
    if (!visible || !detail?.id || activeTab !== 'pcrs') return;
    setLinkedPcrsLoading(true);
    workIdService.getLinkedPcrs(detail.id)
      .then((pcrs) => setLinkedPcrs(pcrs))
      .catch((e) => console.error('Failed to load linked PCRs:', e))
      .finally(() => setLinkedPcrsLoading(false));
  }, [visible, detail?.id, activeTab]);

  useEffect(() => {
    if (!editMode || !visible || !editServiceAreaId || !editForm.assetType) {
      setAssets([]);
      return;
    }
    setAssetsLoading(true);
    // Tighten to the selected foreman area (Ashcroft, etc.)
    assetService
      .searchAssets({ foremanAreaId: Number(editForm.foremanAreaId), assetType: editForm.assetType })
      .then((data) => {
      setAssets(Array.isArray(data) ? data : []);
      setAssetsLoading(false);
    }).catch((e) => {
      console.error('Error loading assets:', e);
      setAssets([]);
      setAssetsLoading(false);
    });
  }, [editMode, visible, editServiceAreaId, editForm.foremanAreaId, editForm.assetType]);

  const handleEditSave = useCallback(async () => {
    if (!detail) return;
    setEditSubmitting(true);
    setError(null);
    try {
      const payload: Parameters<typeof workIdService.update>[1] = {};
      if (editForm.workIdentificationId !== (detail.workIdentificationId ?? '')) payload.workIdentificationId = editForm.workIdentificationId;
      if (editForm.comments !== (detail.comments ?? '')) payload.comments = editForm.comments;
      if (editForm.priority !== (detail.priority ?? '')) payload.priority = editForm.priority;
      if (editForm.foremanAreaId !== '' && Number(editForm.foremanAreaId) !== detail.foremanAreaId) payload.foremanAreaId = Number(editForm.foremanAreaId);
      if (editForm.assetId !== '' && Number(editForm.assetId) !== detail.asset?.id) payload.assetId = Number(editForm.assetId);
      if (
        isAdmin &&
        editForm.reportedOn.trim() !== '' &&
        editForm.reportedOn !== reportedOnToDateInputValue(detail.reportedOn)
      ) {
        payload.reportedOn = editForm.reportedOn.trim();
      }
      if (Object.keys(payload).length === 0) {
        setEditMode(false);
        return;
      }
      await workIdService.update(detail.id, payload);
      await loadDetail(detail.id);
      onSuccess();
      setEditMode(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update work ID');
    } finally {
      setEditSubmitting(false);
    }
  }, [detail, editForm, isAdmin, loadDetail, onSuccess]);

  const formatSource = (w: WorkIdDetail): string => {
    const d = w as { createdFromForm?: { name?: string }; formName?: string };
    if (d.createdFromForm?.name) return `TrueContext - ${d.createdFromForm.name}`;
    if (d.formName) return `DREL - ${d.formName}`;
    return '–';
  };

  if (!visible) return null;

  const title = `Work ID – ${detail?.workIdentificationId ?? workId?.workIdentificationId ?? workId?.id ?? ''}`;
  // A Work ID has at most one linked task.
  const linkedTask = detail?.task ?? null;

  const hasMapCoords =
    (detail?.latitude != null && detail?.longitude != null) ||
    (detail?.asset?.startLatitude != null && detail?.asset?.startLongitude != null);
  const mapLocation = hasMapCoords
    ? (detail?.latitude != null && detail?.longitude != null
        ? { lat: detail.latitude, lng: detail.longitude }
        : detail?.asset?.startLatitude != null && detail?.asset?.startLongitude != null
          ? { lat: detail.asset.startLatitude, lng: detail.asset.startLongitude }
          : undefined)
    : undefined;

  const footer = (
    <>
      {editMode ? (
        <>
          <Button type="button" variant="secondary" onClick={cancelEdit} disabled={editSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleEditSave}
            disabled={editSubmitting}
          >
            {editSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-1" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </>
      ) : (
        <>
          {showEditButton && (
            <Button type="button" variant="outline-primary" onClick={startEdit}>
              Edit
            </Button>
          )}
          {isMerged && canUnmerge && (
            <Button
              type="button"
              variant="warning"
              onClick={() => setUnmergeVisible(true)}
            >
              Unmerge
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      )}
    </>
  );

  return (
    <>
      <ResponsiveModal
        isOpen={visible}
        onClose={onClose}
        title={title}
        footer={footer}
        size="large"
      >
        {loading ? (
          <div className="tw-text-center tw-py-5">
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <Alert variant="danger" className="tw-mb-0">
            {error}
          </Alert>
        ) : detail ? (
          <div
            className="tw-flex tw-flex-col tw-h-full"
            style={{ minHeight: 0 }}
          >
            <div
              className="tw-shrink-0 tw-py-2 tw-px-2 tw-rounded tw-mb-2"
              style={{
                backgroundColor: 'var(--bs-body-bg, #fff)',
                borderBottom: '1px solid var(--bs-border-color, #dee2e6)',
                marginLeft: '-1rem',
                marginRight: '-1rem',
                marginTop: '-1rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                paddingTop: '1rem',
              }}
            >
              <strong className="tw-block tw-mb-2">Status</strong>
              <WorkIdStatusProgress status={detail.status} />
            </div>

            <div
              className="tw-grow tw-overflow-y-auto tw-overflow-x-hidden"
              style={{ minHeight: 0, minWidth: 0 }}
            >
            {error && (
              <Alert variant="danger" className="tw-mb-3">
                {error}
              </Alert>
            )}

            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab((k as 'details' | 'attachments' | 'pcrs') || 'details')}
              className="tw-mb-3"
            >
              <Tab eventKey="details" title="Details">
            {editMode ? (
              <Row>
                <Col md={6}>
                  <Form.Group className="tw-mb-2">
                    <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Work ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.workIdentificationId}
                      onChange={(e) => setEditForm((f) => ({ ...f, workIdentificationId: e.target.value }))}
                      placeholder="Work identification"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="tw-mb-0">
                    <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Reported by</Form.Label>
                    <Form.Control
                      type="text"
                      plaintext
                      readOnly
                      className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                      value={
                        detail.reportedBy
                          ? `${detail.reportedBy.name} ${detail.reportedBy.lastName}`.trim()
                          : '–'
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="tw-mb-0">
                    <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Reported date</Form.Label>
                    {isAdmin ? (
                      <Form.Control
                        type="date"
                        value={editForm.reportedOn}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, reportedOn: e.target.value }))
                        }
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        plaintext
                        readOnly
                        className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                        value={formatReportedOnDisplay(detail.reportedOn)}
                      />
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="tw-mb-2">
                    <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Foreman Area</Form.Label>
                    <Form.Select
                      value={editForm.foremanAreaId === '' ? '' : String(editForm.foremanAreaId)}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          foremanAreaId: e.target.value ? Number(e.target.value) : '',
                          assetId: '',
                        }))
                      }
                    >
                      <option value="">Select foreman area</option>
                      {filterOptions.foremanAreas.map((fa) => (
                        <option key={fa.id} value={fa.id}>
                          {fa.name}
                          {fa.serviceAreaName ? ` (${fa.serviceAreaName})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="tw-mb-2">
                    <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Asset Type</Form.Label>
                    <Form.Select
                      value={editForm.assetType}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditForm((f) => ({ ...f, assetType: v, assetId: '' }));
                      }}
                    >
                      {(Object.values(ASSET_TYPES) as string[]).map((type) => (
                        <option key={type} value={type}>
                          {ASSET_TYPE_LABELS[type as keyof typeof ASSET_TYPE_LABELS] ?? type}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="tw-mb-2">
                    <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Asset</Form.Label>
                    {assetsLoading ? (
                      <div className="tw-text-content-muted tw-text-sm">Loading assets…</div>
                    ) : editServiceAreaId ? (
                      <Form.Select
                        value={editForm.assetId === '' ? '' : String(editForm.assetId)}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            assetId: e.target.value ? Number(e.target.value) : '',
                          }))
                        }
                      >
                        <option value="">Select asset (optional)</option>
                        {assets.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name} (#{a.assetNumber})
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control type="text" plaintext readOnly className="tw-bg-surface-alt" value="Select foreman area first" />
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="tw-mb-2">
                    <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Priority</Form.Label>
                    <Form.Select
                      value={editForm.priority ?? ''}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          priority: e.target.value,
                        }))
                      }
                    >
                      {PRIORITY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group className="tw-mb-2">
                    <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Comments</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editForm.comments}
                      onChange={(e) => setEditForm((f) => ({ ...f, comments: e.target.value }))}
                      placeholder="Comments"
                    />
                  </Form.Group>
                </Col>
              </Row>
            ) : (
            <>
            <Row>
              <Col md={6}>
                <Form.Group className="tw-mb-0">
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Reported by</Form.Label>
                  <Form.Control
                    type="text"
                    plaintext
                    readOnly
                    className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                    value={
                      detail.reportedBy
                        ? `${detail.reportedBy.name} ${detail.reportedBy.lastName}`.trim()
                        : '–'
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="tw-mb-0">
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Reported date</Form.Label>
                  <Form.Control
                    type="text"
                    plaintext
                    readOnly
                    className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                    value={formatReportedOnDisplay(detail.reportedOn)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <WorkIdResponseTimer createdAtIso={detail.createdAt ?? detail.reportedOn} />
              </Col>
              <Col md={6}>
                <Form.Group className="tw-mb-0">
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Asset</Form.Label>
                  <Form.Control
                    type="text"
                    plaintext
                    readOnly
                    className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                    value={detail.asset?.name ?? '–'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="tw-mb-0">
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Foreman Area</Form.Label>
                  <Form.Control
                    type="text"
                    plaintext
                    readOnly
                    className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                    value={
                      detail.foremanArea
                        ? `${detail.foremanArea.name}${detail.foremanArea.serviceArea ? ` (${detail.foremanArea.serviceArea.code})` : ''}`
                        : '–'
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="tw-mb-0">
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Priority</Form.Label>
                  <div className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2">
                    {detail.priority ? (
                      <Badge bg={getPriorityVariant(detail.priority)}>
                        {formatPriority(detail.priority)}
                      </Badge>
                    ) : (
                      '–'
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="tw-mb-0">
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Source</Form.Label>
                  <Form.Control
                    type="text"
                    plaintext
                    readOnly
                    className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                    value={formatSource(detail)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group className="tw-mb-0">
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    plaintext
                    readOnly
                    rows={readOnlyCommentsTextareaRows(detail.comments)}
                    className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                    value={detail.comments ?? '–'}
                    style={{
                      resize: 'none',
                      maxHeight: 'min(40vh, 320px)',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="tw-mb-0">
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Linked task</Form.Label>
                  {linkedTask ? (
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <Link
                          to={taskPageUrl(linkedTask, true)}
                          className={`tw-grow tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2 tw-no-underline tw-text-black ${linkedTaskHover ? 'tw-border-primary tw-bg-primary tw-bg-opacity-10' : ''}`}
                          title="View task (you can return to Work IDs from that page)"
                          onMouseEnter={() => setLinkedTaskHover(true)}
                          onMouseLeave={() => setLinkedTaskHover(false)}
                        >
                          {[linkedTask.taskNumber ?? linkedTask.id, linkedTask.type]
                            .filter(Boolean)
                            .join(' · ')}
                        </Link>
                        {detail.status?.toUpperCase() === 'ASSIGNED' && canAssign && (
                          <Button
                            variant="warning"
                            size="sm"
                            title="Remove Work ID from task"
                            disabled={taskUnassignId !== null}
                            onClick={() => handleRemoveFromTask(linkedTask.id)}
                          >
                            {taskUnassignId === linkedTask.id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <span>Remove</span>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Form.Control
                      type="text"
                      plaintext
                      readOnly
                      className="tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2"
                      value="–"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>

            {hasMapCoords && (
              <div className="tw-mt-3">
                <Button
                  type="button"
                  variant={showMap ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setShowMap(!showMap)}
                >
                  {showMap ? 'Hide Map' : 'View in map'}
                </Button>
                {showMap && mapLocation && (
                  <div style={{ height: 240 }} className="tw-rounded tw-border tw-border-line tw-overflow-hidden tw-mt-2">
                    <GoogleGPSMap
                      startLocation={mapLocation}
                      endLocation={mapLocation}
                      onLocationSelect={() => {}}
                      locationType="start"
                      isPointActivity
                      readOnly
                      autoFitBounds={false}
                      hideInstructions
                      height={240}
                    />
                  </div>
                )}
              </div>
            )}

            {!hasMapCoords && (
              <div className="tw-mt-3">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Location</Form.Label>
                <div
                  className="tw-rounded tw-border tw-border-line tw-flex tw-items-center tw-justify-center tw-text-content-muted"
                  style={{ height: 120, backgroundColor: '#e9ecef' }}
                >
                  No location data available
                </div>
              </div>
            )}
            </>
            )}
              </Tab>
              <Tab eventKey="attachments" title={`Attachments (${detail.attachments?.length ?? 0})`}>
                <div className="tw-py-2">
                  <div className="tw-border tw-border-line tw-rounded tw-p-3 tw-bg-surface-alt">
                    <WorkIdImageAttachments
                      workIdId={detail.id}
                      attachments={detail.attachments ?? []}
                    />
                  </div>
                </div>
              </Tab>
              <Tab eventKey="pcrs" title="Linked PCRs">
                <div className="tw-py-2">
                  {linkedPcrsLoading ? (
                    <div className="tw-text-center tw-py-4">
                      <Spinner animation="border" size="sm" className="tw-mr-2" />
                      Loading PCRs…
                    </div>
                  ) : linkedPcrs.length === 0 ? (
                    <div className="tw-text-center tw-py-4 tw-text-content-muted">
                      <i className="bi bi-link-45deg tw-text-4xl tw-block tw-mb-2 tw-opacity-50" />
                      No PCRs linked to this Work ID.
                    </div>
                  ) : (
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      {linkedPcrs.map((pcr) => (
                        <Button
                          key={pcr.id}
                          type="button"
                          variant="light"
                          className="tw-border tw-border-line tw-text-left tw-w-full tw-p-2"
                          onClick={() => setSelectedPcr(pcr)}
                        >
                          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                            <span className="tw-font-semibold">{pcr.report_id || `PCR #${pcr.id}`}</span>
                            <Badge bg={pcr.status === 'new' ? 'warning' : pcr.status === 'internal_review' ? 'info' : 'success'} text={pcr.status === 'new' || pcr.status === 'internal_review' ? 'dark' : undefined}>
                              {formatPcrStatusLabel(pcr.status)}
                            </Badge>
                            {pcr.report_type && (
                              <span className="tw-text-content-muted tw-text-sm">{normalizePcrReportTypeBucket(pcr.report_type)}</span>
                            )}
                            <span className="tw-text-content-muted tw-text-sm tw-ml-auto">
                              {pcr.receivedAt ? formatDate(pcr.receivedAt) : ''}
                            </span>
                          </div>
                          {pcr.community_text && (
                            <div className="tw-text-content-muted tw-text-sm tw-mt-1">{pcr.community_text}{pcr.road_text ? ` – ${pcr.road_text}` : ''}</div>
                          )}
                          {pcr.user_name && (
                            <div className="tw-text-content-muted tw-text-sm">{pcr.user_name}</div>
                          )}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>

            {false && (
            <div className="tw-flex tw-gap-1 tw-flex-wrap tw-mb-3">
              {detail?.status?.toUpperCase() === WorkIdStatus.NEW &&
                hasPermission('workId:approve') && (
                  <Button variant="success" size="sm" onClick={() => setApproveVisible(true)}>
                    <i className="bi bi-check-circle tw-mr-1" />
                    Approve
                  </Button>
                )}
              {(detail?.status?.toUpperCase() === WorkIdStatus.NEW ||
                detail?.status?.toUpperCase() === WorkIdStatus.APPROVED) &&
                hasPermission('workId:cancel') && (
                  <Button variant="danger" size="sm" onClick={() => setCancelVisible(true)}>
                    <i className="bi bi-x-circle tw-mr-1" />
                    Cancel Work ID
                  </Button>
                )}
              {detail?.status?.toUpperCase() === WorkIdStatus.CANCELLED &&
                hasPermission('workId:cancel') && (
                  <Button variant="success" size="sm" onClick={() => setReopenVisible(true)}>
                    <i className="bi bi-arrow-counterclockwise tw-mr-1" />
                    Reopen
                  </Button>
                )}
              {hasPermission('workId:history_view') && (
                <Button variant="outline-primary" size="sm" onClick={() => setHistoryVisible(true)}>
                  <i className="bi bi-clock-history tw-mr-1" />
                  History
                </Button>
              )}
            </div>
            )}
            </div>
          </div>
        ) : (
          <p className="tw-text-content-muted tw-mb-0">No work ID selected.</p>
        )}
      </ResponsiveModal>

      <WorkIdHistoryModal
        visible={historyVisible}
        onClose={() => setHistoryVisible(false)}
        workId={detail}
        onAddComment={(updated) => {
          setDetail(updated);
        }}
      />

      <WorkIdApproveModal
        visible={approveVisible}
        onClose={() => setApproveVisible(false)}
        workId={detail}
        onSuccess={() => {
          handleActionSuccess();
          setApproveVisible(false);
        }}
      />

      <WorkIdCancelModal
        visible={cancelVisible}
        onClose={() => setCancelVisible(false)}
        workId={detail}
        onSuccess={() => {
          handleActionSuccess();
          setCancelVisible(false);
        }}
      />

      <WorkIdReopenModal
        visible={reopenVisible}
        onClose={() => setReopenVisible(false)}
        workId={detail}
        onSuccess={() => {
          handleActionSuccess();
          setReopenVisible(false);
        }}
      />

      <WorkIdUnmergeModal
        visible={unmergeVisible}
        onClose={() => setUnmergeVisible(false)}
        workId={detail}
        onSuccess={() => {
          handleActionSuccess();
          setUnmergeVisible(false);
          onClose();
        }}
      />

      <PcrSubmissionDetailModal
        isOpen={selectedPcr !== null}
        submission={selectedPcr}
        canPatchPcrSubmission={
          hasPermission('submission:pcr_update') || hasPermission('workId:create')
        }
        canSetInternalReview={hasPermission('pcr:internal_review')}
        canFinalizePcr={hasPermission('pcr:finalize')}
        onSubmissionUpdated={async () => {
          if (!detail?.id) return;
          try {
            const pcrs = await workIdService.getLinkedPcrs(detail.id);
            setLinkedPcrs(pcrs);
            setSelectedPcr((prev) => (prev ? pcrs.find((p) => p.id === prev.id) ?? prev : null));
          } catch (e) {
            console.error('Failed to refresh linked PCRs:', e);
          }
        }}
        onSaveSubmission={
          selectedPcr
            ? async (input) => {
                const updated = await patchPcrSubmission(selectedPcr.id, input);
                setSelectedPcr(updated);
              }
            : undefined
        }
        onClose={() => setSelectedPcr(null)}
      />

      <Modal show={reasonModal.show} onHide={closeReasonModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {reasonModal.action === 'close' ? 'Close task' : 'Reopen task'}
            {reasonModal.task && (
              <span className="tw-text-content-muted tw-font-normal tw-text-base tw-ml-2">
                (Task #{reasonModal.task.id})
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="tw-mb-0">
            <Form.Label>Reason <span className="tw-text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reasonModal.reason}
              onChange={(e) =>
                setReasonModal((prev) => ({
                  ...prev,
                  reason: e.target.value,
                  error: null,
                }))
              }
              isInvalid={!!reasonModal.error}
              placeholder={
                reasonModal.action === 'close'
                  ? 'Enter reason for closing this task…'
                  : 'Enter reason for reopening this task…'
              }
            />
            {reasonModal.error && (
              <Form.Control.Feedback type="invalid">
                {reasonModal.error}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeReasonModal}>
            Cancel
          </Button>
          <Button
            variant={reasonModal.action === 'close' ? 'danger' : 'success'}
            onClick={submitReasonModal}
            disabled={taskActionId !== null}
          >
            {taskActionId !== null ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-1" />
                Processing…
              </>
            ) : reasonModal.action === 'close' ? (
              'Close task'
            ) : (
              'Reopen task'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={internalReviewModal.show} onHide={() => !internalReviewModal.loading && setInternalReviewModal({ show: false, taskId: null, comments: '', loading: false })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send for Internal Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="tw-mb-0">
            <Form.Label>Comments <span className="tw-text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={internalReviewModal.comments}
              onChange={(e) => setInternalReviewModal((prev) => ({ ...prev, comments: e.target.value }))}
              placeholder="Reason for sending this task to internal review…"
              disabled={internalReviewModal.loading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setInternalReviewModal({ show: false, taskId: null, comments: '', loading: false })}
            disabled={internalReviewModal.loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSetToInternalReview}
            disabled={internalReviewModal.loading || !internalReviewModal.comments.trim()}
          >
            {internalReviewModal.loading ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-1" />
                Submitting…
              </>
            ) : (
              'Send to Internal Review'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WorkIdDetailModal;
